/**
 * Select a Launch Queue.
 */
import {
  GREEN_550,
  RED_450,
  THEME,
} from '@wandb/weave/common/css/globals.styles';
import {Select} from '@wandb/weave/components/Form/Select';
import {Icon, IconMagicWandStick} from '@wandb/weave/components/Icon';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React from 'react';
import {type FormatOptionLabelMeta} from 'react-select';
import styled from 'styled-components';

import {
  RunQueuePrioritizationMode,
  type TemplateVariable,
} from '../../generated/graphql';
import {isPublicQueue} from './publicQueue/utils';
import {ResourceChip} from './ResourceChip';
import * as S from './SelectComponents.styles';
import {type JsonObject, type ResourceType} from './utilTypes';

type CircleProps = {
  isActive: boolean;
};

export const Circle = styled.div<CircleProps>`
  border-radius: 50%;
  width: 22px;
  height: 22px;
  background-color: ${props =>
    props.isActive
      ? `light-dark(color-mix(in srgb, ${GREEN_550} 15%, transparent), color-mix(in srgb, var(--green-800) 15%, transparent))`
      : `light-dark(color-mix(in srgb, ${RED_450} 16%, transparent), color-mix(in srgb, var(--red-800) 16%, transparent))`};
  color: ${props => (props.isActive ? THEME.statusSuccessText : THEME.statusErrorText)};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
`;

export type QueueOptions = QueueOption | GroupedQueueOptions;

export type QueueOption = {
  isActive: boolean;
  value: string;
  entity: string;
  project: string;
  queue: string;
  queueID: string;
  defaultResourceConfigID: string;
  prioritizationMode: RunQueuePrioritizationMode;
  resourceType: ResourceType;
  config: JsonObject;
  variables: TemplateVariable[];
  disabled: boolean;
  additionalServices: {id: string; name: string}[];
  fullConfig: JsonObject;
};

// This option represents creating a new Starter queue
export const CREATE_QUEUE_PLACEHOLDER: QueueOption = {
  isActive: true,
  value: '',
  entity: '',
  project: '',
  queue: '',
  queueID: '',
  defaultResourceConfigID: '',
  prioritizationMode: RunQueuePrioritizationMode.Disabled,
  resourceType: 'local-container',
  config: {},
  variables: [],
  disabled: false,
  additionalServices: [],
  fullConfig: {},
};

export type GroupedQueueOptions = {
  label: string;
  options: QueueOption[];
};

type SelectQueueProps = {
  options: QueueOptions[];
  selectedOption: QueueOption | null;
  onSelectQueue: (option: QueueOption) => void;
};

const renderOptionLabel = (
  props: QueueOption,
  meta: FormatOptionLabelMeta<QueueOption>
) => {
  const {isActive, entity, queue, resourceType} = props;
  if (queue === '') {
    return (
      <S.SelectOptionLabel>
        <IconMagicWandStick style={{marginRight: 8}} />
        <S.SelectOptionLabel>Create a 'Starter' queue</S.SelectOptionLabel>
        <ResourceChip value="local-container" />
      </S.SelectOptionLabel>
    );
  }

  const isValue = meta.context === 'value'; // selected value, not menu
  const label = isValue ? `${entity}/${queue}` : queue;
  const icon = isActive ? 'checkmark-circle' : 'warning';
  const tooltip = isActive
    ? 'Queue has at least one running agent'
    : 'You can add items to this queue but it has no running agents';

  return (
    <S.SelectOption data-test={queue}>
      <Tooltip
        content={tooltip}
        trigger={
          <Circle isActive={isActive}>
            <Icon name={icon} width={14} height={14} />
          </Circle>
        }
      />
      <S.SelectOptionLabel>{label}</S.SelectOptionLabel>
      <ResourceChip value={resourceType} />
    </S.SelectOption>
  );
};

export const SelectQueue = ({
  options,
  selectedOption,
  onSelectQueue,
}: SelectQueueProps) => {
  const onChange = (option: QueueOption | null) => {
    if (option) {
      onSelectQueue(option);
    }
  };

  const flatOptions = React.useMemo(() => {
    return options.flatMap(opt => ('options' in opt ? opt.options : [opt]));
  }, [options]);

  // disable the selector if there is only one public queue available
  // and it's the currently selected option
  const isDisabled = React.useMemo(() => {
    if (
      !selectedOption ||
      !isPublicQueue(selectedOption.queue, selectedOption.entity)
    ) {
      return false;
    }
    const publicQueues = flatOptions.filter(option =>
      isPublicQueue(option.queue, option.entity)
    );
    return publicQueues.length === 1;
  }, [selectedOption, flatOptions]);

  return (
    <Select<QueueOption, false, GroupedQueueOptions>
      isOptionDisabled={q => q.disabled}
      options={options}
      value={selectedOption}
      onChange={onChange}
      isDisabled={isDisabled}
      formatOptionLabel={renderOptionLabel}
      placeholder="Select a Launch queue"
      noOptionsMessage={({inputValue}) => {
        return !inputValue ? 'No queue available' : 'No results found';
      }}
    />
  );
};
